/**
 * SchoolGarden — single-polygon editor for is_school users.
 *
 * Route: /schulgarten (protected: is_school)
 *
 * UI flow:
 *   1. If no garden yet → Leaflet draw mode (polygon).
 *      Constraints: max ~1 hectare. Frontend pre-checks area before save.
 *   2. If a garden exists → show on map + simplified snapshot card.
 *      Two tabs: "Schul-Sicht" (kid-friendly recommendations) and
 *      "Daten-Sicht" (weather/soil-moisture mini-chart).
 *      Big red "Neu zeichnen" button to delete + restart.
 *
 * Reuses Leaflet + leaflet-draw which are already a dependency (MyFields).
 */
import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Tree, Drop, Sun, FlowerTulip as Bee, Leaf, Trash, ArrowClockwise, MapPin,
  ChartLineUp, Sparkle, Plant as Seedling,
} from "@phosphor-icons/react";
import {
  saveSchoolGarden, fetchMySchoolGarden, deleteSchoolGarden,
  fetchSchoolGardenSnapshot,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import PhotoDiary from "@/components/school/PhotoDiary";

const MAX_AREA_M2 = 10_000;       // 1 hectare — hard UX cap (backend enforces 12 000)
const ANCHOR = [49.5, 11.5];       // Mitteldeutschland default centre

// Quick & dirty equirectangular area (m²) — same projection logic as the backend.
function polygonAreaM2(coords) {
  if (!coords || coords.length < 3) return 0;
  const latMid = coords.reduce((s, p) => s + p[1], 0) / coords.length;
  const cosLat = Math.cos((latMid * Math.PI) / 180);
  const mLat = 111_320;
  const mLon = 111_320 * cosLat;
  const pts = coords.map(([lon, lat]) => [lon * mLon, lat * mLat]);
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
}

export default function SchoolGarden() {
  const { user } = useAuth();
  const [garden, setGarden] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("kid");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const g = await fetchMySchoolGarden();
        if (!mounted) return;
        setGarden(g);
        if (g) {
          const snap = await fetchSchoolGardenSnapshot();
          if (mounted) setSnapshot(snap);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!user?.is_school) {
    return (
      <div className="max-w-xl mx-auto p-6 text-sm text-white" data-testid="school-garden-blocked">
        <p>Diese Seite ist nur für Schulen mit aktivem Schul-Code zugänglich.</p>
        <Link to="/school" className="text-[#00FA9A] hover:underline mt-3 inline-block">→ Schul-Code eingeben</Link>
      </div>
    );
  }

  const onSaved = async (newGarden) => {
    setGarden(newGarden);
    setSnapshot(null);
    try {
      const snap = await fetchSchoolGardenSnapshot();
      setSnapshot(snap);
    } catch {
      /* snapshot fetch failures shouldn't block the editor */
    }
  };

  const onDeleteGarden = async () => {
    if (!window.confirm("Wirklich löschen und ein neues Polygon zeichnen?")) return;
    await deleteSchoolGarden();
    setGarden(null);
    setSnapshot(null);
    toast.success("Schulgarten gelöscht — du kannst ein neues Polygon zeichnen.");
  };

  return (
    <>
      <Helmet>
        <title>Schulgarten · Bodenblick</title>
      </Helmet>
      <div className="space-y-6" data-testid="school-garden-root">
        <header>
          <div className="eid-label inline-flex items-center gap-1.5">
            <Tree size={11} weight="fill" className="text-[#00FA9A]" />
            Schule · {user.school_label || "Schulgarten"}
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tighter mt-1 flex items-center gap-2 leading-[0.95]">
            <Seedling size={28} className="text-[#00FA9A]" /> Euer Schulgarten
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Zeichnet euren Schulhof / Schulgarten ein — Bodenblick zeigt euch
            danach jeden Tag Wetter, Boden und „heute ein guter Tag für…".
            Maximal 1 Hektar (10.000 m²), genau ein Polygon pro Schule.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
          <DrawMap
            existing={garden}
            onSaved={onSaved}
            onDelete={onDeleteGarden}
          />
          <aside className="space-y-3" data-testid="school-garden-sidebar">
            {loading ? (
              <div className="eid-card p-6 text-sm text-zinc-400">Lade…</div>
            ) : garden ? (
              <GardenSnapshotCard
                garden={garden}
                snapshot={snapshot}
                tab={tab}
                setTab={setTab}
                onForceReload={async () => {
                  const snap = await fetchSchoolGardenSnapshot(true);
                  setSnapshot(snap);
                  toast.success("Frisch geladen");
                }}
                onDelete={onDeleteGarden}
              />
            ) : (
              <div className="eid-card p-6 text-sm text-zinc-400" data-testid="school-garden-empty">
                <p className="text-base font-medium text-white mb-2 flex items-center gap-2">
                  <MapPin size={16} className="text-[#00FA9A]" /> Noch nichts gezeichnet.
                </p>
                <p>
                  Klick links oben auf das Polygon-Symbol und zeichne den Umriss
                  eures Schulgartens. Doppelklick zum Abschließen.
                </p>
                <p className="mt-2 text-[12px] text-zinc-500">
                  Tipp: maximal 1 Hektar — fokussiert euch auf den eigentlichen
                  Garten, nicht das ganze Schulgelände.
                </p>
              </div>
            )}
            <div className="text-[11px] text-zinc-500 font-mono px-2">
              <Link to="/schulgaerten" className="hover:text-[#00FA9A] inline-flex items-center gap-1">
                <MapPin size={11} /> alle Schulgärten in DE/AT anschauen
              </Link>
            </div>
          </aside>
        </div>

        {garden && <PhotoDiary />}
      </div>
    </>
  );
}

function DrawMap({ existing, onSaved, onDelete }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawnLayer = useRef(null);
  const existingLayer = useRef(null);
  const drawControlRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [label, setLabel] = useState("Unser Schulgarten");
  const [saving, setSaving] = useState(false);

  // Initialize map ONCE.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (containerRef.current._leaflet_id) delete containerRef.current._leaflet_id;
    const map = L.map(containerRef.current, { center: ANCHOR, zoom: 6 });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "© OpenStreetMap",
    }).addTo(map);
    drawnLayer.current = new L.FeatureGroup().addTo(map);
    existingLayer.current = new L.FeatureGroup().addTo(map);

    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: "#00FA9A", fillColor: "#7EE8B0" },
        },
        polyline: false, rectangle: false, circle: false,
        marker: false, circlemarker: false,
      },
      edit: false,
    });
    map.addControl(drawControl);
    drawControlRef.current = drawControl;

    map.on(L.Draw.Event.CREATED, (e) => {
      drawnLayer.current.clearLayers();
      drawnLayer.current.addLayer(e.layer);
      const latlngs = e.layer.getLatLngs()[0];
      const coords = latlngs.map((p) => [+p.lng.toFixed(5), +p.lat.toFixed(5)]);
      setPending({ coords });
    });

    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* */ } mapRef.current = null; };
  }, []);

  // Show the existing garden polygon read-only, and disable drawing.
  useEffect(() => {
    if (!mapRef.current) return;
    existingLayer.current.clearLayers();
    drawnLayer.current?.clearLayers();
    setPending(null);
    if (existing && existing.polygon?.length) {
      const latlngs = existing.polygon.map(([lon, lat]) => [lat, lon]);
      const poly = L.polygon(latlngs, {
        color: "#FFFFFF",
        weight: 3,
        fillOpacity: 0.28,
        fillColor: "#00FA9A",
      });
      poly.bindTooltip(existing.label || "Schulgarten", { permanent: false });
      poly.addTo(existingLayer.current);
      mapRef.current.fitBounds(poly.getBounds(), { padding: [40, 40] });
      // Hide draw control while a garden exists
      if (drawControlRef.current) {
        try { mapRef.current.removeControl(drawControlRef.current); } catch { /* */ }
      }
    } else if (drawControlRef.current) {
      try { mapRef.current.addControl(drawControlRef.current); } catch { /* */ }
    }
  }, [existing]);

  const onSave = async () => {
    if (!pending) return;
    const area = polygonAreaM2(pending.coords);
    if (area > MAX_AREA_M2) {
      toast.error(`Polygon ist zu groß (${Math.round(area)} m²). Max. ${MAX_AREA_M2} m².`);
      return;
    }
    if (area < 5) {
      toast.error("Polygon ist zu klein. Zeichne den Garten etwas größer.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveSchoolGarden({
        label: label.trim() || "Unser Schulgarten",
        polygon: pending.coords,
      });
      if (res.ok && res.garden) {
        toast.success(`Schulgarten gespeichert (${Math.round(res.garden.area_m2)} m²)`);
        drawnLayer.current?.clearLayers();
        setPending(null);
        onSaved(res.garden);
      } else {
        toast.error("Konnte Schulgarten nicht speichern.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte Schulgarten nicht speichern.");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    drawnLayer.current?.clearLayers();
    setPending(null);
  };

  const pendingArea = pending ? polygonAreaM2(pending.coords) : 0;
  const overLimit = pendingArea > MAX_AREA_M2;

  return (
    <div className="eid-card overflow-hidden" data-testid="school-garden-map">
      <div ref={containerRef} className="w-full h-[480px] bg-white/5" />
      {pending && !existing && (
        <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="school-garden-pending">
          <div className="flex-1 min-w-0">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Name eures Schulgartens"
              className="w-full px-3 py-2 text-sm border border-white/10 rounded-sm bg-[#121212]"
              maxLength={80}
              data-testid="school-garden-label-input"
            />
            <div className={`text-[11px] font-mono mt-1 ${overLimit ? "text-[#FF7A45]" : "text-zinc-500"}`}>
              Fläche: {Math.round(pendingArea)} m² {overLimit ? `· über Limit (${MAX_AREA_M2} m²)` : ""}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm border border-white/10 rounded-sm hover:bg-white/5"
            data-testid="school-garden-cancel"
          >
            Abbrechen
          </button>
          <button
            onClick={onSave}
            disabled={saving || overLimit}
            className="px-4 py-2 text-sm bg-[#00FA9A] text-black rounded-sm disabled:opacity-40 hover:bg-[#00FA9A]/20"
            data-testid="school-garden-save"
          >
            {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      )}
      {existing && (
        <div className="p-3 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between gap-3" data-testid="school-garden-existing-bar">
          <div className="text-[12px] text-zinc-400">
            <strong className="text-white">{existing.label}</strong> · {Math.round(existing.area_m2)} m²
            · gespeichert {(existing.created_at || "").slice(0, 10)}
          </div>
          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-xs border border-[#FF5500]/40 text-[#FF7A45] rounded-sm hover:bg-[#FF6A1F] hover:text-white inline-flex items-center gap-1"
            data-testid="school-garden-redraw"
          >
            <Trash size={12} /> Neu zeichnen
          </button>
        </div>
      )}
    </div>
  );
}

function GardenSnapshotCard({ garden, snapshot, tab, setTab, onForceReload, onDelete }) {
  if (!snapshot) {
    return <div className="eid-card p-6 text-sm text-zinc-400">Lade Wetter & Boden…</div>;
  }
  const s = snapshot.snapshot || {};
  const m = s.moisture || {};
  const r = s.recommendation || {};
  const t = s.temperature_today_c;

  return (
    <div className="eid-card overflow-hidden" data-testid="school-garden-snapshot">
      <div className="flex border-b border-white/10 bg-[#0A0A0A]">
        <TabButton active={tab === "kid"} onClick={() => setTab("kid")} testid="tab-kid">
          <Sparkle size={12} weight="fill" /> Schul-Sicht
        </TabButton>
        <TabButton active={tab === "data"} onClick={() => setTab("data")} testid="tab-data">
          <ChartLineUp size={12} /> Daten-Sicht
        </TabButton>
        <button
          onClick={onForceReload}
          className="ml-auto px-3 text-[10px] uppercase tracking-widest font-mono text-zinc-400 hover:text-white inline-flex items-center gap-1"
          title="Frisch laden"
          data-testid="school-garden-reload"
        >
          <ArrowClockwise size={11} /> aktualisieren
        </button>
      </div>

      {tab === "kid" ? (
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Heute gut für</div>
            <div className="mt-1 text-2xl font-bold text-white leading-tight">{r.verb || "rausgehen"}.</div>
            <p className="text-sm text-white/70 leading-snug mt-1">{r.detail}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniMetric
              Icon={Sun}
              accent="#FFC24B"
              label="Heute"
              value={t != null ? `${t.toFixed(1)} °C` : "—"}
              hint={t != null && t >= 12 ? "Bienen-Wetter" : t != null && t < 0 ? "Frost" : "kühl"}
            />
            <MiniMetric
              Icon={Drop}
              accent="#8B7CF6"
              label="Boden"
              value={m.label || "—"}
              hint={m.advice}
            />
            <MiniMetric
              Icon={Bee}
              accent={s.bee_active ? "#FFC24B" : "#8A8A93"}
              label="Bienenflug"
              value={s.bee_active ? "aktiv" : "ruhend"}
              hint={s.bee_active ? "Wildbienen zählen!" : "wartet auf Wärme"}
            />
            <MiniMetric
              Icon={Leaf}
              accent="#00FA9A"
              label="Garten"
              value={`${Math.round(garden.area_m2)} m²`}
              hint={garden.label}
            />
          </div>
          <button
            onClick={onDelete}
            className="text-[11px] text-[#FF7A45] hover:underline inline-flex items-center gap-1"
            data-testid="school-garden-snapshot-delete"
          >
            <Trash size={11} /> Polygon löschen / neu zeichnen
          </button>
        </div>
      ) : (
        <div className="p-5 space-y-4" data-testid="school-garden-data-tab">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Boden-Feuchte (0–10 cm)</div>
            <SparkLine values={(s.weather_series?.soil_moisture_0_7cm || []).map((v) => v == null ? null : v * 100)} unit="%" color="#8B7CF6" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Temperatur (Tagesmittel)</div>
            <SparkLine values={s.weather_series?.temperature_2m_mean || []} unit=" °C" color="#FF5500" />
          </div>
          <div className="text-[11px] font-mono text-zinc-500 pt-2 border-t border-white/10">
            Quelle: Open-Meteo ({s.source}) · 7 Tage · gecached 3 h
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-[11px] uppercase tracking-widest font-mono inline-flex items-center gap-1.5 transition-colors ${
        active ? "bg-[#121212] text-white" : "text-zinc-400 hover:text-white"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function MiniMetric({ Icon, accent, label, value, hint }) {
  return (
    <div className="border border-white/15 p-3" style={{ borderLeftWidth: 3, borderLeftColor: accent }}>
      <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono flex items-center gap-1">
        <Icon size={11} weight="fill" style={{ color: accent }} /> {label}
      </div>
      <div className="text-lg font-semibold leading-tight mt-1">{value}</div>
      {hint && <div className="text-[10px] text-white/60 leading-snug mt-0.5">{hint}</div>}
    </div>
  );
}

function SparkLine({ values, unit = "", color = "#00FA9A" }) {
  const clean = (values || []).filter((v) => v != null);
  if (!clean.length) return <div className="text-[11px] text-zinc-500 mt-1">keine Daten</div>;
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const span = Math.max(0.01, max - min);
  const W = 220, H = 44, pad = 4;
  const step = (W - 2 * pad) / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    if (v == null) return null;
    const x = pad + i * step;
    const y = H - pad - ((v - min) / span) * (H - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).filter(Boolean);
  return (
    <div className="mt-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12">
        <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.6" />
      </svg>
      <div className="flex justify-between text-[10px] font-mono text-zinc-500">
        <span>{min.toFixed(1)}{unit}</span>
        <span>{max.toFixed(1)}{unit}</span>
      </div>
    </div>
  );
}
